/**
 * Tier Sale Progress Tracker
 * Matches the discount logic exactly - tracks tier-sale products and tier-tagged rewards
 */

class TierSaleTracker {
    constructor() {
      this.progressBar = null;
      this.isInitialized = false;
      this.maxTiers = 6;
      this.debugEnabled = Boolean(window?.__TIER_SALE_DEBUG__);
      this.productTagCache = new Map();
      this.productDataCache = new Map(); // /products/{handle}.js payloads for the preview popup
      this.previewCard = null; // { productId, tier } of the card the open preview belongs to
      this.previewHandle = null; // Guards async populate against the preview switching products
      this.previewImages = [];
      this.previewRawImages = []; // Unsized URLs, used to match variant featured images
      this.previewImageIndex = 0;
      this.cartDataCache = { data: null, fetchedAt: 0 };
      this.cacheTTL = 1500; // ms cache window to reduce duplicate fetches
      this.lastCartData = null;
      this.updateTierProgress = this.updateTierProgress.bind(this);
      this.isUpdatingTiers = false; // Race condition protection
      this.activeTierFilter = 'all'; // Tier filter pill state ('all' or '1'-'6')
      this.activeSizeFilter = 'all'; // Size filter state ('all' or a size value like '2XL')
      this.activeSort = 'featured'; // Sort state: featured | tier-asc | tier-desc | name-asc | name-desc
      this.updateTimeout = null; // Overall update timeout
      this.stuckStateRecoveryTimeout = null; // Recovery mechanism
      
      // Duplicate allowance settings (will be read from data attributes)
      this.tierDuplicateSettings = {
        1: false, // Default: Tiers 1, 4 and 6 cannot be duplicated
        2: true,
        3: true,
        4: false,
        5: true,
        6: false
      };

      // Strict slots only accept their own tier's items (will be read from data attributes)
      // Mirrors the discount app's per-tier "Strict (only own tier items)" toggle
      this.tierStrictSettings = {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: true // Default: slot 6 only accepts TIER6 items
      };
      
      this.init();
    }

    logDebug(...args) {
      if (!this.debugEnabled) return;
      console.log(...args);
    }
  
    init() {
      // Wait for cart drawer to be available
      setTimeout(() => {
        this.updateTierProgress();
        this.setupEventListeners();
      }, 500);
    }


  
    isTierSaleEnabled() {
      // Check if the tier sale feature is enabled in theme settings
      // This checks if the progress bar element exists, which means the Liquid template rendered it
      // The Liquid template only renders if settings.enable_tier_sale is true
      return document.querySelector('[data-tier-sale-progress]') !== null;
    }
  
    setupEventListeners() {
      // Theme-specific cart events
      const cartEvents = [
        'theme:cart:added',
        'theme:cart:loaded', 
        'theme:cart:updated',
        'cart:updated',
        'cart:item-added',
        'cart:item-removed'
      ];
      
      cartEvents.forEach(eventName => {
        document.addEventListener(eventName, () => {
          this.handleCartChange();
        });
      });
  
      // Cart drawer interactions
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-quantity-button], [data-item-remove], [data-cart-item-remove]')) {
          this.handleCartChange(500);
        }
        
        if (e.target.matches('[data-add-to-cart], button[type="submit"]')) {
          this.handleCartChange(800);
        }
      });
  
      // Quantity changes
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="updates[]"], [data-quantity-field]')) {
          this.handleCartChange(300);
        }
      });
  
      // Tier rewards dropdown events
      this.setupDropdownEventListeners();
  
      // MutationObserver for DOM changes
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const target = mutation.target;
            if (target.closest('.cart-drawer__items, .cart__items, #cartItemsHolder')) {
              shouldUpdate = true;
            }
          }
        });
        
        if (shouldUpdate) {
          this.handleCartChange(200);
        }
      });
  
      // Observe cart container changes
      const cartContainer = document.querySelector('.cart-drawer__items, .cart__items, #cartItemsHolder');
      if (cartContainer) {
        observer.observe(cartContainer, {
          childList: true,
          subtree: true
        });
      }
    }
  
    setupDropdownEventListeners() {
      document.addEventListener('click', (e) => {
        // Show dropdown when browse rewards button is clicked
        if (e.target.matches('[data-tier-sale-browse-rewards]') || e.target.closest('[data-tier-sale-browse-rewards]')) {
          e.preventDefault();
          this.showRewardsDropdown();
        }
        
        // Hide dropdown when backdrop or close button is clicked
        if (e.target.matches('[data-tier-rewards-backdrop], [data-tier-rewards-close]') || e.target.closest('[data-tier-rewards-close]')) {
          this.hideRewardsDropdown();
        }

        // Tier filter pills
        const filterPill = e.target.closest('[data-tier-filter]');
        if (filterPill) {
          this.setTierFilter(filterPill.dataset.tierFilter);
        }

        // Filter carousel arrows
        if (e.target.closest('[data-tier-filter-prev]')) {
          this.scrollFilterCarousel(-1);
        }
        if (e.target.closest('[data-tier-filter-next]')) {
          this.scrollFilterCarousel(1);
        }

        // Sort menu
        const sortOption = e.target.closest('[data-tier-sort-option]');
        if (e.target.closest('[data-tier-sort-toggle]')) {
          this.toggleSortMenu();
        } else if (sortOption) {
          this.setSortOption(sortOption.dataset.tierSortOption);
        } else if (!e.target.closest('[data-tier-sort]')) {
          // Any click outside the sort control closes the menu
          this.closeSortMenu();
        }

        // Size filter menu
        const sizeOption = e.target.closest('[data-tier-size-option]');
        if (e.target.closest('[data-tier-size-toggle]')) {
          this.toggleSizeMenu();
        } else if (sizeOption) {
          this.setSizeFilter(sizeOption.dataset.tierSizeOption);
        } else if (!e.target.closest('[data-tier-size-filter]')) {
          this.closeSizeMenu();
        }

        // Product preview popup
        if (e.target.closest('[data-tier-preview-close]')) {
          this.closeProductPreview();
        } else if (e.target.closest('[data-tier-preview-prev]')) {
          this.stepPreviewImage(-1);
        } else if (e.target.closest('[data-tier-preview-next]')) {
          this.stepPreviewImage(1);
        } else if (e.target.closest('[data-tier-preview-add]')) {
          this.addFromPreview();
        } else if (!e.target.closest('[data-tier-preview]')) {
          // Tapping a reward card anywhere except its own controls opens the preview
          const card = e.target.closest('.tier-reward-product');
          const isCardControl = e.target.closest('a, select, [data-tier-reward-add]');
          if (card && !isCardControl && !card.classList.contains('tier-progress-loading')) {
            this.openProductPreview(card);
          }
        }
      });

      // Preview variant picker: push the choice back onto the card's select so
      // Add (which submits the card's form) uses it, and show variant imagery
      document.addEventListener('change', (e) => {
        if (!e.target.matches?.('[data-tier-preview-variant]') || !this.previewCard) return;

        const card = document.querySelector(
          `.tier-reward-product[data-product-id="${this.previewCard.productId}"][data-tier="${this.previewCard.tier}"]`
        );
        const cardSelect = card?.querySelector('[data-variant-select]');
        if (cardSelect) {
          cardSelect.value = e.target.value;
          cardSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Jump the gallery to the variant's own image when it has one
        const product = this.productDataCache.get(this.previewHandle);
        const variant = product?.variants?.find(v => String(v.id) === e.target.value);
        const variantImage = variant?.featured_image?.src;
        if (variantImage && this.previewRawImages.length > 1) {
          const normalize = (url) => (url || '').replace(/^https?:/, '');
          const index = this.previewRawImages.findIndex(src => normalize(src) === normalize(variantImage));
          if (index >= 0) {
            this.previewImageIndex = index;
            this.setPreviewImage(document.querySelector('[data-tier-preview]'));
          }
        }
      });

      // Filter carousel: track scroll position for arrow visibility.
      // Document-level capture listener survives cart-drawer re-renders,
      // which replace the track element (scroll events don't bubble)
      document.addEventListener('scroll', (e) => {
        if (e.target?.matches?.('[data-tier-filter-container]')) {
          this.updateFilterArrows();
        }
      }, { capture: true, passive: true });

      // Map vertical mouse wheel to horizontal scroll over the pill row on desktop
      document.addEventListener('wheel', (e) => {
        const track = e.target?.closest?.('[data-tier-filter-container]');
        if (!track) return;
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll > 0 && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          track.scrollLeft += e.deltaY;
        }
      }, { passive: false });

      window.addEventListener('resize', () => this.updateFilterArrows(), { passive: true });
  
      // Handle escape key: close the product preview first, then the sort menu, then the dropdown
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const preview = document.querySelector('[data-tier-preview]:not([hidden])');
          if (preview) {
            this.closeProductPreview();
            return;
          }
          const sizeMenu = document.querySelector('[data-tier-size-menu]');
          if (sizeMenu && sizeMenu.style.display !== 'none') {
            this.closeSizeMenu();
            return;
          }
          const sortMenu = document.querySelector('[data-tier-sort-menu]');
          if (sortMenu && sortMenu.style.display !== 'none') {
            this.closeSortMenu();
            return;
          }
          const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
          if (dropdown) {
            this.hideRewardsDropdown();
          }
        }
      });
  
      // Note: Cart event listeners are already set up in setupEventListeners()
      // Removed duplicate listeners to prevent race conditions
  
      // Handle tier reward add to cart with enhanced loading states
      document.addEventListener('submit', (e) => {
        if (e.target.matches('[data-tier-reward-form]')) {
          e.preventDefault();
          
          // Prevent concurrent operations
          if (this.isUpdating) {
            this.logDebug('⏸️ Cart operation already in progress, ignoring click');
            return;
          }
          
          const form = e.target;
          const product = form.closest('.tier-reward-product');
          const addButton = form.querySelector('[data-tier-reward-add]');
          
          // Check duplicate restrictions before allowing add to cart
          const productTier = parseInt(product.dataset.tier);
          const allowDuplicates = product.dataset.tierAllowDuplicates === 'true';
          const tierClaimed = product.dataset.tierClaimed === 'true';
          
          if (!allowDuplicates && tierClaimed) {
            this.logDebug(`⛔ Tier ${productTier} has already been claimed and duplicates are not allowed`);
            // Show error feedback
            this.showTierRewardError(product, addButton, 'This tier reward has already been claimed');
            setTimeout(() => {
              this.resetTierRewardState(product, addButton);
            }, 2000);
            return;
          }
          
          // Block further operations
          this.isUpdating = true;
          
          // Show enhanced loading state
          this.showTierRewardLoading(product, addButton);
          
          const formData = new FormData(form);
          
          // Add to cart via fetch
          fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'Accept': 'application/json'
            }
          })
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            this.logDebug('✅ Tier reward added to cart:', data);
            
            // Show success state
            this.showTierRewardSuccess(product, addButton);
            
            // Trigger cart update events that the theme listens to
            document.dispatchEvent(new CustomEvent('theme:cart:added', { detail: data }));
            document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
            
            // Update our tier progress
            setTimeout(() => this.updateTierProgress(), 100);
            
            // Reset to added state after success animation
            setTimeout(() => {
              this.setTierRewardAdded(product, addButton);
              this.isUpdating = false; // Allow new operations
            }, 2000);
          })
          .catch(error => {
            console.error('❌ Error adding tier reward to cart:', error);
            
            // Show error and reset button
            this.showTierRewardError(product, addButton, error.message);
            setTimeout(() => {
              this.resetTierRewardState(product, addButton);
              this.isUpdating = false; // Allow new operations
            }, 3000);
          });
        }
      });
    }
  
    setupSearchFunctionality() {
      const searchInput = document.querySelector('[data-tier-search]');
      const searchClear = document.querySelector('[data-tier-search-clear]');

      if (!searchInput) return;

      // Event listeners
      searchInput.addEventListener('input', () => {
        this.applyFilters();
      });

      if (searchClear) {
        searchClear.addEventListener('click', () => {
          searchInput.value = '';
          this.applyFilters();
          searchInput.focus();
        });
      }

      // Store for clearing later
      this.clearSearch = () => {
        searchInput.value = '';
        this.applyFilters();
      };
    }

    // Apply search term AND tier filter pill to the product list
    applyFilters() {
      const searchInput = document.querySelector('[data-tier-search]');
      const searchClear = document.querySelector('[data-tier-search-clear]');
      const products = document.querySelectorAll('.tier-reward-product');
      const productsContainer = document.querySelector('.tier-rewards-dropdown__products');
      const noResultsContainer = document.querySelector('[data-no-results-container]');
      const noRewardsContainer = document.querySelector('[data-no-rewards-container]');
      const noEligibleContainer = document.querySelector('[data-no-eligible-container]');

      const term = (searchInput?.value || '').toLowerCase().trim();
      const tierFilter = this.activeTierFilter;
      const soldOutContainer = document.querySelector('[data-tier-sold-out-container]');
      let visibleCount = 0;

      // A qualified tier with zero rendered cards is sold out (Liquid only renders available variants)
      const tierIsSoldOut = tierFilter !== 'all' &&
        document.querySelector(`.tier-reward-product[data-tier="${tierFilter}"]`) === null;

      if (tierIsSoldOut) {
        products.forEach(product => {
          if (!product.classList.contains('tier-product--hidden')) {
            product.style.display = 'none';
          }
        });
        if (productsContainer) productsContainer.style.display = 'none';
        if (noResultsContainer) noResultsContainer.style.display = 'none';
        if (noRewardsContainer) noRewardsContainer.style.display = 'none';
        if (noEligibleContainer) noEligibleContainer.style.display = 'none';
        if (soldOutContainer) {
          const soldOutTitle = soldOutContainer.querySelector('[data-tier-sold-out-title]');
          if (soldOutTitle) soldOutTitle.textContent = `Tier ${tierFilter} Sold Out`;
          soldOutContainer.style.display = 'block';
        }
        if (searchClear) {
          searchClear.style.display = term !== '' ? 'flex' : 'none';
        }
        return;
      }

      if (soldOutContainer) soldOutContainer.style.display = 'none';

      products.forEach(product => {
        // Skip if product is already hidden by tier qualification
        if (product.classList.contains('tier-product--hidden')) {
          return;
        }

        const matchesTier = tierFilter === 'all' || product.dataset.tier === tierFilter;

        let matchesSearch = true;
        if (term !== '') {
          const title = product.querySelector('.tier-reward-product__title');
          if (!title) return;

          // Get text without the tier label
          const tierLabel = title.querySelector('.tier-reward-product__tier-label');
          let titleText = title.textContent.toLowerCase();
          if (tierLabel) {
            titleText = titleText.replace(tierLabel.textContent.toLowerCase(), '').trim();
          }
          matchesSearch = titleText.includes(term);
        }

        let matchesSize = true;
        if (this.activeSizeFilter !== 'all') {
          const productSizes = (product.dataset.sizes || '').split(',').filter(Boolean);
          matchesSize = productSizes.includes(this.activeSizeFilter);
        }

        if (matchesTier && matchesSearch && matchesSize) {
          product.style.display = '';
          visibleCount++;
        } else {
          product.style.display = 'none';
        }
      });

      // Exactly one list state shows at a time:
      // matches -> product grid, filtered to zero -> "no matches", nothing eligible at all -> "no eligible"
      const isFiltering = term !== '' || tierFilter !== 'all' || this.activeSizeFilter !== 'all';
      if (visibleCount === 0) {
        if (productsContainer) productsContainer.style.display = 'none';
        if (noRewardsContainer) noRewardsContainer.style.display = 'none';
        if (noResultsContainer) noResultsContainer.style.display = isFiltering ? 'block' : 'none';
        if (noEligibleContainer) noEligibleContainer.style.display = isFiltering ? 'none' : 'block';
      } else {
        if (productsContainer) productsContainer.style.display = 'grid';
        if (noResultsContainer) noResultsContainer.style.display = 'none';
        if (noEligibleContainer) noEligibleContainer.style.display = 'none';
      }

      // Show/hide clear button
      if (searchClear) {
        searchClear.style.display = term !== '' ? 'flex' : 'none';
      }
    }

    setTierFilter(value) {
      this.activeTierFilter = value;

      const pills = document.querySelectorAll('[data-tier-filter]');
      pills.forEach(pill => {
        const isActive = pill.dataset.tierFilter === value;
        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');

        // Keep the selected pill visible in the carousel
        if (isActive && typeof pill.scrollIntoView === 'function') {
          pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      this.applyFilters();
    }

    toggleSortMenu() {
      const menu = document.querySelector('[data-tier-sort-menu]');
      const toggle = document.querySelector('[data-tier-sort-toggle]');
      if (!menu || !toggle) return;

      const isOpen = menu.style.display !== 'none';
      menu.style.display = isOpen ? 'none' : 'block';
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }

    closeSortMenu() {
      const menu = document.querySelector('[data-tier-sort-menu]');
      const toggle = document.querySelector('[data-tier-sort-toggle]');
      if (menu) menu.style.display = 'none';
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    setSortOption(value) {
      this.activeSort = value;

      document.querySelectorAll('[data-tier-sort-option]').forEach(option => {
        option.classList.toggle('is-active', option.dataset.tierSortOption === value);
      });

      this.closeSortMenu();
      this.applySort();
    }

    toggleSizeMenu() {
      const menu = document.querySelector('[data-tier-size-menu]');
      const toggle = document.querySelector('[data-tier-size-toggle]');
      if (!menu || !toggle) return;

      const isOpen = menu.style.display !== 'none';
      menu.style.display = isOpen ? 'none' : 'block';
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }

    closeSizeMenu() {
      const menu = document.querySelector('[data-tier-size-menu]');
      const toggle = document.querySelector('[data-tier-size-toggle]');
      if (menu) menu.style.display = 'none';
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    setSizeFilter(value) {
      this.activeSizeFilter = value;

      document.querySelectorAll('[data-tier-size-option]').forEach(option => {
        option.classList.toggle('is-active', option.dataset.tierSizeOption === value);
      });

      // Empty label keeps the chip icon-only until a size is chosen
      const label = document.querySelector('[data-tier-size-label]');
      if (label) label.textContent = value === 'all' ? '' : value;
      const toggle = document.querySelector('[data-tier-size-toggle]');
      if (toggle) toggle.classList.toggle('is-filtering', value !== 'all');

      this.closeSizeMenu();
      this.applyFilters();

      // Pre-select the matching variant on every card so Add uses the filtered size
      if (value !== 'all') {
        this.autoSelectSizeVariants(value);
      }
    }

    // Variant titles join options with " / " - match a whole segment, not a substring
    // (a plain includes() would match size "S" inside "XS")
    autoSelectSizeVariants(size) {
      document.querySelectorAll('.tier-reward-product [data-variant-select]').forEach(select => {
        const match = Array.from(select.options).find(option =>
          option.text.split('/').map(part => part.trim()).includes(size)
        );
        if (match && select.value !== match.value) {
          select.value = match.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // Rebuild the size menu from in-stock sizes on eligible cards; hide the
    // control when nothing sized is on offer. Resets a filter that no longer applies.
    updateSizeFilterOptions() {
      const control = document.querySelector('[data-tier-size-filter]');
      const menu = document.querySelector('[data-tier-size-menu]');
      if (!control || !menu) return;

      const sizes = [];
      document.querySelectorAll('.tier-reward-product:not(.tier-product--hidden)').forEach(card => {
        (card.dataset.sizes || '').split(',').filter(Boolean).forEach(size => {
          if (!sizes.includes(size)) sizes.push(size);
        });
      });

      // Canonical apparel order; unknown values keep first-seen order after the known ones
      const SIZE_ORDER = ['XXS', 'XS', 'S', 'SMALL', 'M', 'MEDIUM', 'L', 'LARGE', 'XL', 'X-LARGE', 'XXL', '2XL', 'XXXL', '3XL', '4XL', '5XL', '6XL', '7XL'];
      const sizeRank = (size) => {
        const index = SIZE_ORDER.indexOf(size.trim().toUpperCase());
        return index === -1 ? SIZE_ORDER.length : index;
      };
      sizes.sort((a, b) => sizeRank(a) - sizeRank(b));

      if (sizes.length === 0) {
        control.style.display = 'none';
        if (this.activeSizeFilter !== 'all') {
          this.setSizeFilter('all');
        }
        return;
      }

      control.style.display = '';
      if (this.activeSizeFilter !== 'all' && !sizes.includes(this.activeSizeFilter)) {
        this.setSizeFilter('all');
      }

      menu.innerHTML = '';
      const options = [['all', 'All Sizes']].concat(sizes.map(size => [size, size]));
      options.forEach(([value, labelText]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tier-rewards-dropdown__sort-option' + (this.activeSizeFilter === value ? ' is-active' : '');
        button.setAttribute('data-tier-size-option', value);
        button.textContent = labelText;
        menu.appendChild(button);
      });
    }

    // Product title without the "Tier N" label prefix (same extraction as search)
    getProductSortTitle(product) {
      const title = product.querySelector('.tier-reward-product__title');
      if (!title) return '';

      const tierLabel = title.querySelector('.tier-reward-product__tier-label');
      let text = title.textContent.toLowerCase();
      if (tierLabel) {
        text = text.replace(tierLabel.textContent.toLowerCase(), '');
      }
      return text.trim();
    }

    // Reorder product cards in the grid according to the active sort.
    // Order is independent of filtering - hidden cards move too, harmlessly.
    applySort() {
      const grid = document.querySelector('.tier-rewards-dropdown__products');
      if (!grid) return;

      const products = Array.from(grid.querySelectorAll('.tier-reward-product'));
      if (!products.length) return;

      // Capture the server-rendered order the first time we see each card
      // (fresh cards from a cart-drawer re-render always arrive in featured order)
      products.forEach((product, index) => {
        if (!product.dataset.featuredIndex) {
          product.dataset.featuredIndex = String(index);
        }
      });

      const sorted = products.slice().sort((a, b) => {
        const featured = parseInt(a.dataset.featuredIndex) - parseInt(b.dataset.featuredIndex);
        switch (this.activeSort) {
          case 'tier-asc':
            return (parseInt(a.dataset.tier) - parseInt(b.dataset.tier)) || featured;
          case 'tier-desc':
            return (parseInt(b.dataset.tier) - parseInt(a.dataset.tier)) || featured;
          case 'name-asc':
            return this.getProductSortTitle(a).localeCompare(this.getProductSortTitle(b)) || featured;
          case 'name-desc':
            return this.getProductSortTitle(b).localeCompare(this.getProductSortTitle(a)) || featured;
          default:
            return featured;
        }
      });

      sorted.forEach(product => grid.appendChild(product));
    }

    // Scroll the pill carousel by most of a viewport width in the given direction (-1/1)
    scrollFilterCarousel(direction) {
      const track = document.querySelector('[data-tier-filter-container]');
      if (!track) return;
      track.scrollBy({ left: direction * Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
    }

    // Show each arrow only when the carousel can scroll further in that direction
    updateFilterArrows() {
      const track = document.querySelector('[data-tier-filter-container]');
      const prevArrow = document.querySelector('[data-tier-filter-prev]');
      const nextArrow = document.querySelector('[data-tier-filter-next]');
      if (!track || !prevArrow || !nextArrow) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      const isOverflowing = maxScroll > 2;

      prevArrow.style.display = isOverflowing && track.scrollLeft > 2 ? 'flex' : 'none';
      nextArrow.style.display = isOverflowing && track.scrollLeft < maxScroll - 2 ? 'flex' : 'none';
    }

    // Pills show for every qualified tier. Sold-out tiers (no rendered cards) stay
    // visible with a dot indicator; tiers hidden only by slot eligibility are hidden.
    // Resets to 'all' if the active pill's tier is no longer shown.
    updateTierFilterPills(qualifiedTier) {
      const container = document.querySelector('[data-tier-filter-container]');
      if (!container) return;

      let activeStillVisible = this.activeTierFilter === 'all';

      container.querySelectorAll('[data-tier-filter]').forEach(pill => {
        const value = pill.dataset.tierFilter;
        if (value === 'all') return;

        const tierNum = parseInt(value);
        const isQualified = tierNum <= qualifiedTier;
        const hasCards = document.querySelector(`.tier-reward-product[data-tier="${value}"]`) !== null;
        const hasVisibleCards = document.querySelector(`.tier-reward-product[data-tier="${value}"]:not(.tier-product--hidden)`) !== null;
        const isSoldOut = isQualified && !hasCards;

        const showPill = isQualified && (hasVisibleCards || isSoldOut);
        pill.style.display = showPill ? '' : 'none';
        pill.classList.toggle('is-sold-out', isSoldOut);

        if (showPill && value === this.activeTierFilter) {
          activeStillVisible = true;
        }
      });

      if (!activeStillVisible) {
        this.setTierFilter('all');
      } else {
        this.applyFilters();
      }

      // Pill visibility changes the track width, so re-evaluate the arrows
      this.updateFilterArrows();

      // Eligible cards changed, so the offered sizes may have too
      this.updateSizeFilterOptions();
    }
  
    async handleCartChange(delay = 300) {

      
      // Race condition protection
      if (this.isUpdatingTiers) {
        this.logDebug('⚠️ Tier update already in progress, debouncing...');
        // Force clear any stuck loading states from previous operation
        this.clearAllLoadingStates();
      }
      
      // Show immediate loading feedback for progress bar AND dropdown
      this.showProgressLoading();
      this.showDropdownCartLoading();
      
      // Invalidate cart cache on incoming cart change to ensure freshness
      this.cartDataCache = { data: null, fetchedAt: 0 };
      
      
      // Clear any existing timeouts
      this.clearAllTimeouts();
      
      // Set up stuck state recovery (if update takes too long)
      this.stuckStateRecoveryTimeout = setTimeout(() => {
        console.error('⚠️ Tier update appears stuck, forcing recovery...');
        this.forceRecoveryFromStuckState();
      }, 15000); // 15 second timeout
      
      // Debounce the actual update with the specified delay
      this.cartChangeTimeout = setTimeout(async () => {
        try {
          // Set race condition flag
          this.isUpdatingTiers = true;
          
          // Set overall update timeout (10 seconds max)
          this.updateTimeout = setTimeout(() => {
            console.error('⚠️ Tier update timeout exceeded, forcing cleanup...');
            this.forceRecoveryFromStuckState();
          }, 10000);
          
          await this.updateTierProgress();
          
          // If modal is open, also refresh modal availability
          const isModalOpen = document.querySelector('.tier-rewards-dropdown.is-active');
          if (isModalOpen) {
          await this.refreshModalAvailability(this.lastCartData);
          }
          
          // COMPREHENSIVE CLEANUP - Clear ALL loading states
          this.clearAllLoadingStates();
          this.clearUpdateState();
          

          
        } catch (error) {
          console.error('❌ Error in cart change handler:', error);
          this.forceRecoveryFromStuckState();
        }
      }, delay);
    }
  
        async updateTierProgress() {
      try {
        this.progressBar = document.querySelector('[data-tier-sale-progress]');
        if (!this.progressBar) {
          this.hideProgressLoading();
          this.clearUpdateState();
          return;
        }
        
        // Read duplicate allowance settings from data attributes
        this.loadDuplicateSettings();
        
        // Check if tier sale is enabled in theme settings
        if (!this.isTierSaleEnabled()) {
          this.hideProgressLoading();
          this.clearUpdateState();
          return;
        }

        // Get current cart data
        const cartData = await this.getCartData();
        if (!cartData) {
          this.hideProgressLoading();
          this.clearUpdateState();
          return;
        }
  
        // Count tier-sale items (matching discount logic: hasTierSaleTag: true)
        const tierSaleCount = await this.countTierSaleItems(cartData);
        
        // Calculate qualified tier (same logic as discount system)
        const qualifiedTier = this.calculateQualifiedTier(tierSaleCount);
        
        // Count tier-tagged products in cart
        const tierData = await this.countTierTaggedProducts(cartData);
        const tierCounts = tierData.counts;
        const tierProductIds = tierData.productIds;
        
        // Track which tiers have been claimed (for UI rendering only)
        const tierClaimed = {
          1: tierCounts[1] > 0,
          2: tierCounts[2] > 0,
          3: tierCounts[3] > 0,
          4: tierCounts[4] > 0,
          5: tierCounts[5] > 0,
          6: tierCounts[6] > 0
        };

        // Debug logging
        this.logDebug('🔍 Tier Sale Update:', {
          tierCounts,
          tierClaimed,
          qualifiedTier
        });
        
        // Calculate slot system data (no duplicate restrictions - products in cart always fill slots)
        const slotData = this.calculateSlotSystem(tierCounts, qualifiedTier);
        
        // Calculate available rewards
        const rewardData = this.calculateAvailableRewards(qualifiedTier, tierCounts, slotData);
        
        // Update the UI
        await this.updateProgressUI(tierSaleCount, qualifiedTier, rewardData, tierProductIds, tierClaimed);
        
        // Hide progress loading state
        this.hideProgressLoading();
        
      } catch (error) {
        console.error('❌ Error updating tier progress:', error);
        this.hideProgressLoading();
        
        // Don't clear update state here - let handleCartChange manage it
        // This allows the timeout recovery to work properly
      }
    }
    
    loadDuplicateSettings() {
      if (!this.progressBar) return;

      // Tier 6 drops out of the sale while its reward pool is sold out - Liquid stamps the effective max
      const maxTiers = parseInt(this.progressBar.getAttribute('data-max-tiers'), 10);
      this.maxTiers = maxTiers >= 1 ? maxTiers : 6;

      // Read settings from data attributes - must be explicitly 'true' to allow duplicates
      this.tierDuplicateSettings[1] = this.progressBar.getAttribute('data-tier-1-allow-duplicates') === 'true';
      this.tierDuplicateSettings[2] = this.progressBar.getAttribute('data-tier-2-allow-duplicates') === 'true';
      this.tierDuplicateSettings[3] = this.progressBar.getAttribute('data-tier-3-allow-duplicates') === 'true';
      this.tierDuplicateSettings[4] = this.progressBar.getAttribute('data-tier-4-allow-duplicates') === 'true';
      this.tierDuplicateSettings[5] = this.progressBar.getAttribute('data-tier-5-allow-duplicates') === 'true';
      this.tierDuplicateSettings[6] = this.progressBar.getAttribute('data-tier-6-allow-duplicates') === 'true';

      // Strict slot settings - must be explicitly 'true' to restrict the slot to its own tier
      this.tierStrictSettings[1] = this.progressBar.getAttribute('data-tier-1-strict') === 'true';
      this.tierStrictSettings[2] = this.progressBar.getAttribute('data-tier-2-strict') === 'true';
      this.tierStrictSettings[3] = this.progressBar.getAttribute('data-tier-3-strict') === 'true';
      this.tierStrictSettings[4] = this.progressBar.getAttribute('data-tier-4-strict') === 'true';
      this.tierStrictSettings[5] = this.progressBar.getAttribute('data-tier-5-strict') === 'true';
      this.tierStrictSettings[6] = this.progressBar.getAttribute('data-tier-6-strict') === 'true';
    }
  
    // Helper method for fetch with timeout
    async fetchWithTimeout(url, options = {}, timeout = 5000) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeout}ms`);
        }
        throw error;
      }
    }
  
    async getCartData(forceRefresh = false) {
      // Short-lived cache to avoid duplicate /cart.json requests during rapid updates
      const now = Date.now();
      if (!forceRefresh && this.cartDataCache.data && (now - this.cartDataCache.fetchedAt) < this.cacheTTL) {
        this.lastCartData = this.cartDataCache.data;
        return this.cartDataCache.data;
      }
      try {
        const response = await this.fetchWithTimeout('/cart.json', {}, 5000);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        this.cartDataCache = { data, fetchedAt: Date.now() };
        this.lastCartData = data;
        return data;
      } catch (error) {
        console.error('❌ Error fetching cart data:', error);
        return null;
      }
    }

    async getProductTags(cartItem) {
      try {
        if (cartItem?.product_tags) {
          return cartItem.product_tags;
        }
        
        const productHandle = cartItem?.handle;
        if (!productHandle) return [];
        
        if (this.productTagCache.has(productHandle)) {
          return this.productTagCache.get(productHandle);
        }
        
        const response = await this.fetchWithTimeout(`/products/${productHandle}.js`, {}, 3000);
        if (!response.ok) return [];
        
        const productData = await response.json();
        const tags = productData.tags || [];
        this.productTagCache.set(productHandle, tags);
        return tags;
      } catch (error) {
        this.logDebug('⚠️ Could not get tags for product:', cartItem?.handle, error.message);
        return [];
      }
    }
  
    async countTierSaleItems(cartData) {
      let count = 0;
      
      for (const item of cartData.items) {
        // Check if product has tier-sale tag (corresponds to hasTierSaleTag: true in discount)
        const hasTierSaleTag = await this.checkProductHasTierSaleTag(item);
        if (hasTierSaleTag) {
          count += item.quantity;
        }
      }
      
      return count;
    }
  
    async checkProductHasTierSaleTag(cartItem) {
      const tags = await this.getProductTags(cartItem);
        return tags.includes('tier-sale');
    }
  
    calculateQualifiedTier(tierSaleCount) {
      // Exact same logic as discount system: direct mapping, capped at max tiers
      return Math.min(this.maxTiers, tierSaleCount);
    }
  
    async countTierTaggedProducts(cartData) {
      const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
      const productIds = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
      
      for (const item of cartData.items) {
        const tierTags = await this.getProductTierTags(item);
        
        // Count quantity for each tier tag found and track product IDs
        tierTags.forEach(tier => {
          if (counts[tier] !== undefined) {
            counts[tier] += item.quantity;
            // Track which products are in cart for each tier
            if (!productIds[tier].includes(item.product_id)) {
              productIds[tier].push(item.product_id);
            }
          }
        });
      }
      
      return { counts, productIds };
    }
  
    async getProductTierTags(cartItem) {
      try {
        const tierTags = [];
        const tags = await this.getProductTags(cartItem);
        
        // Check for tier tags (TIER1, TIER2, TIER3, TIER4, TIER5, TIER6)
        if (tags.includes('TIER1')) tierTags.push(1);
        if (tags.includes('TIER2')) tierTags.push(2);
        if (tags.includes('TIER3')) tierTags.push(3);
        if (tags.includes('TIER4')) tierTags.push(4);
        if (tags.includes('TIER5')) tierTags.push(5);
        if (tags.includes('TIER6')) tierTags.push(6);
        
        return tierTags;
      } catch (error) {
        this.logDebug('⚠️ Could not get tier tags for product:', cartItem?.handle, error.message);
        return [];
      }
    }
  
    calculateSlotSystem(tierCounts, qualifiedTier) {
      // Exact replica of Shopify function extension slot-filling logic
      // Products already in cart ALWAYS count as filling slots
      // Duplicate restrictions are NOT checked here - they only affect UI rendering
      const totalSlots = qualifiedTier;
      
      // Create product quantities object like the function extension
      const productQuantities = {};
      
      // Convert tierCounts to productQuantities format
      // Include ALL products in cart (they count as filling slots regardless of duplicate settings)
      for (let tier = 1; tier <= this.maxTiers; tier++) {
        if (tierCounts[tier] > 0) {
          productQuantities[`tier_${tier}`] = {
            quantity: tierCounts[tier],
            tier: tier
          };
        }
      }
      
      let filledSlots = 0;
      
      // Simulate exact function extension slot-filling algorithm
      // Products already in cart fill slots regardless of duplicate restrictions
      for (let slot = 1; slot <= qualifiedTier; slot++) {
        // Find products that can fill this slot (tier <= slot) and are available
        // Strict slots only accept their own tier's items - lower tiers do not carry up
        const availableProducts = Object.values(productQuantities).filter(product => {
          if (product.quantity <= 0) return false;
          if (this.tierStrictSettings[slot]) return product.tier === slot;
          return product.tier <= slot;
        });
        
        if (availableProducts.length > 0) {
          // Sort by tier (highest first) - same as function extension
          availableProducts.sort((a, b) => b.tier - a.tier);
          const bestProduct = availableProducts[0];
          
          // Use this product for this slot
          bestProduct.quantity--;
          filledSlots++;
          
          // Remove product if quantity hits 0
          if (bestProduct.quantity === 0) {
            const productKey = Object.keys(productQuantities).find(
              key => productQuantities[key] === bestProduct
            );
            delete productQuantities[productKey];
          }
        }
      }
      
      const availableSlots = Math.max(0, totalSlots - filledSlots);
      
      return {
        totalSlots,
        filledSlots,
        availableSlots
      };
    }
  
    calculateAvailableRewards(qualifiedTier, tierCounts, slotData) {
      // Simple slot-based calculation
      const totalClaimed = slotData.filledSlots;
      const unclaimedRewards = slotData.availableSlots;
      const maxPossibleRewards = slotData.totalSlots;
      

      
      return {
        totalAvailable: unclaimedRewards,
        totalClaimed,
        unclaimedRewards,
        tierBreakdown: {}, // No longer needed with slot system
        maxPossibleRewards
      };
    }
  
    async updateProgressUI(tierSaleCount, qualifiedTier, rewardData, tierProductIds, tierClaimed = {}) {
      if (!this.progressBar) return;
  
      // Update data attributes
      this.progressBar.setAttribute('data-tier-sale-items', tierSaleCount);
      this.progressBar.setAttribute('data-qualified-tier', qualifiedTier);
      
      // Update claimed status - always set to ensure false values are properly set
      this.progressBar.setAttribute('data-tier-1-claimed', tierClaimed[1] === true ? 'true' : 'false');
      this.progressBar.setAttribute('data-tier-2-claimed', tierClaimed[2] === true ? 'true' : 'false');
      this.progressBar.setAttribute('data-tier-3-claimed', tierClaimed[3] === true ? 'true' : 'false');
      this.progressBar.setAttribute('data-tier-4-claimed', tierClaimed[4] === true ? 'true' : 'false');
      this.progressBar.setAttribute('data-tier-5-claimed', tierClaimed[5] === true ? 'true' : 'false');
      this.progressBar.setAttribute('data-tier-6-claimed', tierClaimed[6] === true ? 'true' : 'false');

      // Update progress percentage - bar ends exactly at achieved tier marker
      let progressPercentage = 0;
      if (qualifiedTier === 0) {
        progressPercentage = 0;
      } else {
        // Progress bar reaches exactly to the achieved tier marker
        // Markers are evenly spaced from 0% to 100% (0%, 20%, 40%, ... for 6 tiers)
        progressPercentage = Math.min(100, (qualifiedTier - 1) * (100 / (this.maxTiers - 1)));
      }
      const progressFill = this.progressBar.querySelector('.tier-sale-progress__fill');
      if (progressFill) {
        progressFill.style.width = `${progressPercentage}%`;
        progressFill.setAttribute('data-progress', progressPercentage);
      }
  
      // Update status text
      const statusElement = this.progressBar.querySelector('.tier-sale-progress__status');
      if (statusElement) {
        let statusText = '';
        if (qualifiedTier === 0) {
          statusText = 'Add tier-sale items to unlock rewards';
        } else if (qualifiedTier >= this.maxTiers) {
          if (rewardData.unclaimedRewards > 0) {
            statusText = `All tiers unlocked! ${rewardData.unclaimedRewards} reward${rewardData.unclaimedRewards !== 1 ? 's' : ''} available`;
          } else {
            statusText = 'All tiers unlocked! All rewards claimed 🎉';
          }
        } else {
          const nextTier = qualifiedTier + 1;
          const itemsNeeded = nextTier - tierSaleCount;
          statusText = `Tier ${qualifiedTier}/${this.maxTiers} unlocked • ${itemsNeeded} More Tub for Tier ${nextTier}`;
        }
        statusElement.textContent = statusText;
      }
  
      // Update tier markers
      const markers = this.progressBar.querySelectorAll('.tier-sale-progress__marker');
      markers.forEach((marker, index) => {
        const tierNum = index + 1;
        marker.className = 'tier-sale-progress__marker';
        
        if (tierNum <= qualifiedTier) {
          marker.classList.add('tier-sale-progress__marker--completed');
        } else if (tierNum === qualifiedTier + 1 && qualifiedTier < this.maxTiers) {
          marker.classList.add('tier-sale-progress__marker--current');
        }
      });
  
      // Update rewards alert
      this.updateRewardsAlert(rewardData);
      
      // Update modal subtitle if modal is open
      this.updateModalSubtitle(rewardData, qualifiedTier);
      
      // Update modal content if modal is open
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (dropdown) {
        await this.updateModalContent(rewardData, qualifiedTier, tierClaimed);
      }
    }
  
    updateRewardsAlert(rewardData) {
      let rewardsAlert = this.progressBar.querySelector('.tier-sale-progress__rewards-alert');
      
      if (rewardData.unclaimedRewards > 0) {
        if (!rewardsAlert) {
          // Create rewards alert if it doesn't exist
          rewardsAlert = this.createRewardsAlert();
          this.progressBar.querySelector('.tier-sale-progress__content').appendChild(rewardsAlert);
        }
        
        // Update rewards count
        const countElement = rewardsAlert.querySelector('.tier-sale-progress__rewards-count');
        if (countElement) {
          countElement.textContent = rewardData.unclaimedRewards;
        }
        
        // Update rewards text
        const textElement = rewardsAlert.querySelector('.tier-sale-progress__rewards-text');
        if (textElement) {
          const pluralText = rewardData.unclaimedRewards !== 1 ? 's' : '';
          textElement.innerHTML = `
            <span class="tier-sale-progress__rewards-count">${rewardData.unclaimedRewards}</span> free reward${pluralText} available
          `;
        }
        
        rewardsAlert.style.display = 'flex';
      } else if (rewardsAlert) {
        rewardsAlert.style.display = 'none';
      }
    }
  
    createRewardsAlert() {
      const alertElement = document.createElement('div');
      alertElement.className = 'tier-sale-progress__rewards-alert';
      alertElement.innerHTML = `
        <div class="tier-sale-progress__rewards-icon">🎁</div>
        <div class="tier-sale-progress__rewards-text">
          <span class="tier-sale-progress__rewards-count">0</span> free rewards available
        </div>
        <button class="tier-sale-progress__rewards-button" data-tier-sale-browse-rewards>
          Browse Rewards
        </button>
      `;
      return alertElement;
    }
  
    showRewardsDropdown() {
      const dropdown = document.querySelector('[data-tier-rewards-dropdown]');
      if (!dropdown) return;
      
      // Setup search functionality when modal opens (ensures elements exist)
      if (!this.searchInitialized) {
        this.setupSearchFunctionality();
        this.searchInitialized = true;
      }
      
      // Clear search, tier filter, size filter, and sort when opening
      if (this.clearSearch) {
        this.clearSearch();
      }
      this.setTierFilter('all');
      this.setSizeFilter('all');
      this.setSortOption('featured');

      dropdown.classList.add('is-active');
      document.body.style.overflow = 'hidden';

      // Reset the pill carousel scroll position (filter reset above ran while hidden)
      const filterContainer = dropdown.querySelector('[data-tier-filter-container]');
      if (filterContainer) {
        filterContainer.scrollLeft = 0;
      }
      // Arrows can only be measured once the modal is visible
      requestAnimationFrame(() => this.updateFilterArrows());

      // Show loading state immediately
      this.showModalLoading();
      
      // Refresh modal to ensure consistent state
      setTimeout(async () => {
        await this.refreshModalAvailability();
        this.hideModalLoading();
      }, 50);
      
      // Focus management for accessibility
      const closeButton = dropdown.querySelector('[data-tier-rewards-close]');
      if (closeButton) {
        setTimeout(() => closeButton.focus(), 100);
      }
    }
  
    hideRewardsDropdown() {
      const dropdown = document.querySelector('[data-tier-rewards-dropdown]');
      if (!dropdown) return;

      // Clear search and close the sort menu and product preview when closing
      if (this.clearSearch) {
        this.clearSearch();
      }
      this.closeSortMenu();
      this.closeProductPreview();

      dropdown.classList.remove('is-active');
      document.body.style.overflow = '';
      
      // Return focus to browse rewards button
      const browseButton = document.querySelector('[data-tier-sale-browse-rewards]');
      if (browseButton) {
        browseButton.focus();
      }
    }

    // Product Preview Popup - opens from a reward card, shows full images + description
    async openProductPreview(card) {
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      const preview = dropdown?.querySelector('[data-tier-preview]');
      if (!preview || !card) return;

      const handle = card.dataset.productHandle;
      const title = card.dataset.productTitle || '';
      const url = card.dataset.productUrl || (handle ? `/products/${handle}` : '#');

      this.previewCard = { productId: card.dataset.productId, tier: card.dataset.tier };
      this.previewHandle = handle;
      this.previewImages = [];
      this.previewRawImages = [];
      this.previewImageIndex = 0;

      // Populate instantly from the card, then upgrade with full product data below
      const titleEl = preview.querySelector('[data-tier-preview-title]');
      const tierEl = preview.querySelector('[data-tier-preview-tier]');
      const linkEl = preview.querySelector('[data-tier-preview-link]');
      const descEl = preview.querySelector('[data-tier-preview-description]');
      const imgEl = preview.querySelector('[data-tier-preview-image]');

      if (titleEl) titleEl.textContent = title;
      if (tierEl) tierEl.textContent = `Tier ${card.dataset.tier}`;
      if (linkEl) linkEl.href = url;
      if (descEl) descEl.innerHTML = '';
      if (imgEl) {
        const cardImg = card.querySelector('.tier-reward-product__image img');
        imgEl.src = cardImg ? cardImg.src : '';
        imgEl.alt = title;
      }
      this.updatePreviewImageNav(preview);

      // Mirror the card's add button state (slot eligibility / duplicate restrictions)
      const cardAdd = card.querySelector('[data-tier-reward-add]');
      const previewAdd = preview.querySelector('[data-tier-preview-add]');
      if (previewAdd) {
        const blocked = !cardAdd || cardAdd.disabled;
        previewAdd.disabled = blocked;
        previewAdd.classList.toggle('tier-reward-product__add-btn--disabled', blocked);
      }

      // Mirror the card's variant select (only in-stock variants) so the
      // variant can be picked here - Add submits the card's form either way
      const cardVariantSelect = card.querySelector('[data-variant-select]');
      const variantRow = preview.querySelector('[data-tier-preview-variant-row]');
      const previewVariantSelect = preview.querySelector('[data-tier-preview-variant]');
      if (variantRow && previewVariantSelect) {
        if (cardVariantSelect) {
          previewVariantSelect.innerHTML = cardVariantSelect.innerHTML;
          previewVariantSelect.value = cardVariantSelect.value;
          variantRow.style.display = '';
        } else {
          previewVariantSelect.innerHTML = '';
          variantRow.style.display = 'none';
        }
      }

      preview.hidden = false;
      const closeButton = preview.querySelector('[data-tier-preview-close][aria-label]');
      if (closeButton) closeButton.focus();

      const data = await this.getPreviewProductData(handle);
      // Bail if the preview was closed or switched products while loading
      if (!data || preview.hidden || this.previewHandle !== handle) return;

      if (descEl && data.description) {
        descEl.innerHTML = data.description;
        this.fixDarkDescriptionText(descEl);
      }
      this.previewRawImages = data.images || [];
      this.previewImages = (data.images || []).map(src => this.sizedImageUrl(src, 600));
      if (this.previewImages.length > 0) {
        this.previewImageIndex = 0;
        this.setPreviewImage(preview);
      }
      this.updatePreviewImageNav(preview);
    }

    closeProductPreview() {
      const preview = document.querySelector('[data-tier-preview]');
      if (!preview || preview.hidden) return;

      preview.hidden = true;
      this.previewHandle = null;

      // Return focus to the originating card's preview trigger when it still exists
      if (this.previewCard) {
        const card = document.querySelector(
          `.tier-reward-product[data-product-id="${this.previewCard.productId}"][data-tier="${this.previewCard.tier}"]`
        );
        const trigger = card?.querySelector('[data-tier-preview-trigger]');
        if (trigger) trigger.focus();
      }

      this.previewCard = null;
      this.previewImages = [];
      this.previewRawImages = [];
      this.previewImageIndex = 0;
    }

    // Route the preview's Add through a real click on the card's own Add button.
    // A programmatic submit skips the click phase, and the theme's cart drawer
    // refresh listens for clicks on [data-atc-trigger] - so click, don't submit.
    addFromPreview() {
      if (!this.previewCard) return;

      const card = document.querySelector(
        `.tier-reward-product[data-product-id="${this.previewCard.productId}"][data-tier="${this.previewCard.tier}"]`
      );
      this.closeProductPreview();

      const addButton = card?.querySelector('[data-tier-reward-add]');
      if (addButton && !addButton.disabled) {
        addButton.click();
      }
    }

    async getPreviewProductData(handle) {
      if (!handle) return null;
      if (this.productDataCache.has(handle)) {
        return this.productDataCache.get(handle);
      }
      try {
        const response = await this.fetchWithTimeout(`/products/${handle}.js`, {}, 5000);
        if (!response.ok) return null;
        const data = await response.json();
        this.productDataCache.set(handle, data);
        return data;
      } catch (error) {
        this.logDebug('⚠️ Could not load preview data for product:', handle, error.message);
        return null;
      }
    }

    sizedImageUrl(src, width) {
      if (!src) return src;
      return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
    }

    // Merchant descriptions are authored for light product pages - inline black
    // or near-black text is unreadable on the dark modal, so strip dark inline
    // colors and let the theme text color apply. Bright accent colors are kept.
    fixDarkDescriptionText(container) {
      container.querySelectorAll('[style*="color"], font[color]').forEach(el => {
        const rgb = getComputedStyle(el).color.match(/\d+(\.\d+)?/g);
        if (!rgb || rgb.length < 3) return;

        const luminance = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
        if (luminance < 100) {
          el.style.color = '';
          el.removeAttribute('color');
        }
      });
    }

    setPreviewImage(preview) {
      const imgEl = preview.querySelector('[data-tier-preview-image]');
      if (imgEl && this.previewImages[this.previewImageIndex]) {
        imgEl.src = this.previewImages[this.previewImageIndex];
      }
      const counter = preview.querySelector('[data-tier-preview-counter]');
      if (counter) {
        counter.textContent = `${this.previewImageIndex + 1} / ${this.previewImages.length}`;
      }
    }

    stepPreviewImage(direction) {
      const preview = document.querySelector('[data-tier-preview]');
      if (!preview || this.previewImages.length < 2) return;

      const count = this.previewImages.length;
      this.previewImageIndex = (this.previewImageIndex + direction + count) % count;
      this.setPreviewImage(preview);
    }

    updatePreviewImageNav(preview) {
      const hasMultiple = this.previewImages.length > 1;
      preview.querySelectorAll('[data-tier-preview-prev], [data-tier-preview-next]').forEach(button => {
        button.style.display = hasMultiple ? 'flex' : 'none';
      });
      const counter = preview.querySelector('[data-tier-preview-counter]');
      if (counter) {
        counter.style.display = hasMultiple ? 'inline-block' : 'none';
        if (hasMultiple) {
          counter.textContent = `${this.previewImageIndex + 1} / ${this.previewImages.length}`;
        }
      }
    }

    updateModalSubtitle(rewardData, qualifiedTier) {
      const modalSubtitle = document.querySelector('[data-tier-rewards-subtitle]');
      if (!modalSubtitle) return; // Modal not open or doesn't exist
      
      const container = document.querySelector('[data-has-rewards]');
      const searchContainer = document.querySelector('[data-tier-search-container]');
      const filterCarousel = document.querySelector('[data-tier-filter-carousel]');

      if (rewardData.unclaimedRewards > 0) {
        const pluralText = rewardData.unclaimedRewards !== 1 ? 's' : '';
        modalSubtitle.textContent = `${rewardData.unclaimedRewards} reward${pluralText} available`;

        // Update data attribute and enable buttons
        if (container) {
          container.setAttribute('data-has-rewards', rewardData.unclaimedRewards);
        }

        // Show search bar and tier filters
        if (searchContainer) {
          searchContainer.style.display = 'flex';
        }
        if (filterCarousel) {
          filterCarousel.style.display = 'block';
          this.updateFilterArrows();
        }

        this.enableRewardButtons();

      } else {
        modalSubtitle.textContent = 'Browse available rewards';

        // Update data attribute and disable buttons
        if (container) {
          container.setAttribute('data-has-rewards', '0');
        }

        // Hide search bar and tier filters when no rewards available
        if (searchContainer) {
          searchContainer.style.display = 'none';
        }
        if (filterCarousel) {
          filterCarousel.style.display = 'none';
        }

        this.disableRewardButtons();
      }
    }
  
    enableRewardButtons() {
      const addButtons = document.querySelectorAll('[data-tier-reward-add]');
      addButtons.forEach(button => {
        button.disabled = false;
        button.classList.remove('tier-reward-product__add-btn--disabled');
      });
    }
  
    disableRewardButtons() {
      const addButtons = document.querySelectorAll('[data-tier-reward-add]');
      addButtons.forEach(button => {
        button.disabled = true;
        button.classList.add('tier-reward-product__add-btn--disabled');
      });
    }
  
    async refreshModalAvailability(cartData = null) {
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (!dropdown) return;

      // Re-sync settings from the latest progress bar render - cart drawer
      // re-renders replace the element (and max tiers can change with tier 6 stock)
      this.progressBar = document.querySelector('[data-tier-sale-progress]') || this.progressBar;
      this.loadDuplicateSettings();

      try {
        // Add timeout protection for modal refresh
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Modal refresh timeout')), 8000);
        });
        
        // Get fresh cart data with timeout protection
        const cartDataPromise = cartData ? Promise.resolve(cartData) : this.getCartData();
        const resolvedCartData = await Promise.race([cartDataPromise, timeoutPromise]);
        if (!resolvedCartData) return;
        this.lastCartData = resolvedCartData;
  

  
        // Re-calculate everything
        const tierSaleCount = await this.countTierSaleItems(resolvedCartData);
        const qualifiedTier = this.calculateQualifiedTier(tierSaleCount);
        const tierData = await this.countTierTaggedProducts(resolvedCartData);
        const tierCounts = tierData.counts;
        
        // Track which tiers have been claimed (for UI rendering only)
        const tierClaimed = {
          1: tierCounts[1] > 0,
          2: tierCounts[2] > 0,
          3: tierCounts[3] > 0,
          4: tierCounts[4] > 0,
          5: tierCounts[5] > 0,
          6: tierCounts[6] > 0
        };

        // Calculate slots (no duplicate restrictions - products in cart always fill slots)
        const slotData = this.calculateSlotSystem(tierCounts, qualifiedTier);
        const rewardData = this.calculateAvailableRewards(qualifiedTier, tierCounts, slotData);
  

  
        // Check for mismatch with progress bar data
        const progressBar = document.querySelector('[data-tier-sale-progress]');
        if (progressBar) {
          const progressTierSaleItems = progressBar.getAttribute('data-tier-sale-items');
          const progressQualifiedTier = progressBar.getAttribute('data-qualified-tier');
          

        }
  
        // Update progress bar attributes first (so they're available if needed)
        if (this.progressBar) {
          this.progressBar.setAttribute('data-tier-1-claimed', tierClaimed[1] === true ? 'true' : 'false');
          this.progressBar.setAttribute('data-tier-2-claimed', tierClaimed[2] === true ? 'true' : 'false');
          this.progressBar.setAttribute('data-tier-3-claimed', tierClaimed[3] === true ? 'true' : 'false');
          this.progressBar.setAttribute('data-tier-4-claimed', tierClaimed[4] === true ? 'true' : 'false');
          this.progressBar.setAttribute('data-tier-5-claimed', tierClaimed[5] === true ? 'true' : 'false');
          this.progressBar.setAttribute('data-tier-6-claimed', tierClaimed[6] === true ? 'true' : 'false');
        }
        
        // Update modal subtitle
        this.updateModalSubtitle(rewardData, qualifiedTier);
        
        // Dynamically update modal content based on rewards availability
        await this.updateModalContent(rewardData, qualifiedTier, tierClaimed, resolvedCartData);
  
      } catch (error) {
        console.error('❌ Error refreshing modal availability:', error);
      }
    }
  
    async updateModalContent(rewardData, qualifiedTier, tierClaimed = {}, cartData = null) {
      const container = document.querySelector('.tier-rewards-dropdown__products-container');
      if (!container) return;
  
      // Update data attribute
      container.setAttribute('data-has-rewards', rewardData.unclaimedRewards);
  
      // Update tier product visibility and button states (pass fresh tierClaimed data)
      await this.updateTierProductVisibility(qualifiedTier, rewardData.unclaimedRewards, tierClaimed, cartData);

      // Re-apply the active sort - a cart-drawer re-render resets cards to featured order
      this.applySort();
      
      // Show/hide main containers
      const productsContainer = container.querySelector('.tier-rewards-dropdown__products');
      const noRewardsContainer = container.querySelector('.tier-rewards-dropdown__no-rewards');
      const noEligibleContainer = container.querySelector('.tier-rewards-dropdown__no-eligible');
      const noResultsContainer = container.querySelector('.tier-rewards-dropdown__no-results');
      const soldOutContainer = container.querySelector('[data-tier-sold-out-container]');

      if (rewardData.unclaimedRewards > 0) {
        // applyFilters() owns the list state (products grid, no-results, sold-out, no-eligible)
        // so exactly one shows at a time
        if (noRewardsContainer) noRewardsContainer.style.display = 'none';
        this.applyFilters();
      } else {
        // All slots claimed - show the "all rewards claimed" state only
        if (productsContainer) productsContainer.style.display = 'none';
        if (noResultsContainer) noResultsContainer.style.display = 'none';
        if (noEligibleContainer) noEligibleContainer.style.display = 'none';
        if (soldOutContainer) soldOutContainer.style.display = 'none';
        if (noRewardsContainer) noRewardsContainer.style.display = 'block';
      }
    }
  
    async updateTierProductVisibility(qualifiedTier, unclaimedRewards, tierClaimed = null, cartData = null) {
      const allTierProducts = document.querySelectorAll('.tier-reward-product');
      
      // If no tiers qualified, hide all products
      if (qualifiedTier === 0) {
        allTierProducts.forEach(product => {
          product.classList.add('tier-product--hidden');
          product.setAttribute('data-tier-qualified', 0);
          product.setAttribute('data-rewards-available', 0);
        });
        this.updateTierFilterPills(0);
        return;
      }
      
      // Get eligible tiers that can fill remaining slots (matching discount logic)
      const eligibleTiers = await this.calculateEligibleTiers(qualifiedTier, cartData);
      
      // Use provided tierClaimed data, or fallback to reading from progress bar
      if (!tierClaimed) {
        tierClaimed = {
          1: this.progressBar?.getAttribute('data-tier-1-claimed') === 'true',
          2: this.progressBar?.getAttribute('data-tier-2-claimed') === 'true',
          3: this.progressBar?.getAttribute('data-tier-3-claimed') === 'true',
          4: this.progressBar?.getAttribute('data-tier-4-claimed') === 'true',
          5: this.progressBar?.getAttribute('data-tier-5-claimed') === 'true',
          6: this.progressBar?.getAttribute('data-tier-6-claimed') === 'true'
        };
      }
      
      allTierProducts.forEach(product => {
        const productTier = parseInt(product.dataset.tier);
        const addButton = product.querySelector('[data-tier-reward-add]');
        
        // Update data attributes
        product.setAttribute('data-tier-qualified', qualifiedTier);
        product.setAttribute('data-rewards-available', unclaimedRewards);
        
        // Update claimed status - ensure it's set as string for proper comparison
        const isClaimed = tierClaimed[productTier] === true;
        product.setAttribute('data-tier-claimed', isClaimed ? 'true' : 'false');
        product.setAttribute('data-tier-allow-duplicates', this.tierDuplicateSettings[productTier] === true ? 'true' : 'false');
        
        // Check duplicate restrictions - must be explicitly true to allow duplicates
        const allowDuplicates = this.tierDuplicateSettings[productTier] === true;
        const blockedByDuplicate = !allowDuplicates && isClaimed;
        
        // Show/hide product based on qualified tier AND eligible tiers
        const isTierQualified = productTier <= qualifiedTier;
        const isTierEligible = eligibleTiers.includes(productTier);
        
        if (isTierQualified && (unclaimedRewards === 0 || isTierEligible)) {
          product.classList.remove('tier-product--hidden');
          
          // Get button container and claimed message elements
          const buttonContainer = product.querySelector('.tier-reward-product__button-container');
          const claimedMessage = product.querySelector('.tier-reward-product__claimed-message');
          
          // Enable/disable button based on available rewards, eligibility, AND duplicate restrictions
          if (unclaimedRewards > 0 && isTierEligible && !blockedByDuplicate) {
            product.classList.remove('tier-product--disabled');
            if (addButton) {
              addButton.disabled = false;
              addButton.classList.remove('tier-reward-product__add-btn--disabled');
              const addText = addButton.querySelector('.tier-reward-product__add-text');
              if (addText) addText.textContent = 'Add';
            }
          } else {
            product.classList.add('tier-product--disabled');
            if (addButton) {
              addButton.disabled = true;
              addButton.classList.add('tier-reward-product__add-btn--disabled');
              const addText = addButton.querySelector('.tier-reward-product__add-text');
              if (addText) addText.textContent = 'Add';
            }
          }
        } else {
          product.classList.add('tier-product--hidden');
        }
      });

      // Refresh filter pills and re-apply search/tier filters against the new visibility
      this.updateTierFilterPills(qualifiedTier);
    }
  
    async calculateEligibleTiers(qualifiedTier, cartData = null) {
      // Determine which tier products can fill remaining slots
      // Slot calculation logic only - duplicate restrictions applied in UI rendering
      try {
        const data = cartData || await this.getCartData();
        if (!data) return [];
  
        // Get current tier counts
        const tierData = await this.countTierTaggedProducts(data);
        const tierCounts = tierData.counts;
        
        // Use the same slot calculation logic (no duplicate restrictions)
        const slotData = this.calculateSlotSystem(tierCounts, qualifiedTier);
        
        // If no slots available, no tiers are eligible
        if (slotData.availableSlots === 0) {
          return [];
        }
        
        // Calculate which slots are currently empty using the same algorithm
        const productQuantities = {};
        
        // Convert tierCounts to productQuantities format
        for (let tier = 1; tier <= this.maxTiers; tier++) {
          if (tierCounts[tier] > 0) {
            productQuantities[`tier_${tier}`] = {
              quantity: tierCounts[tier],
              tier: tier
            };
          }
        }
        
        const eligibleTiers = new Set();
        
        // Simulate slot filling to find empty slots
        for (let slot = 1; slot <= qualifiedTier; slot++) {
          // Strict slots only accept their own tier's items - lower tiers do not carry up
          const availableProducts = Object.values(productQuantities).filter(product => {
            if (product.quantity <= 0) return false;
            if (this.tierStrictSettings[slot]) return product.tier === slot;
            return product.tier <= slot;
          });

          if (availableProducts.length > 0) {
            // Slot can be filled - use the best product
            availableProducts.sort((a, b) => b.tier - a.tier);
            const bestProduct = availableProducts[0];
            bestProduct.quantity--;

            if (bestProduct.quantity === 0) {
              const productKey = Object.keys(productQuantities).find(
                key => productQuantities[key] === bestProduct
              );
              delete productQuantities[productKey];
            }
          } else if (this.tierStrictSettings[slot]) {
            // Empty strict slot - only its own tier can fill it
            eligibleTiers.add(slot);
          } else {
            // Empty slot - any tier <= slot can fill it
            for (let tier = 1; tier <= slot; tier++) {
              eligibleTiers.add(tier);
            }
          }
        }
  
        const result = Array.from(eligibleTiers).sort((a, b) => a - b);
        return result;
        
      } catch (error) {
        console.error('❌ Error calculating eligible tiers:', error);
        // Fallback: show all qualified tiers if there are available slots
        return Array.from({length: qualifiedTier}, (_, i) => i + 1);
      }
    }
  
    async updateProductButtonStates(rewardData) {
      // Get all tier reward product forms in the modal
      const tierRewardForms = document.querySelectorAll('[data-tier-reward-form]');
      
      tierRewardForms.forEach(form => {
        const addButton = form.querySelector('[data-tier-reward-add]');
        if (!addButton) return;
        
        // Simple logic: Can add if there are available slots
        const canAddMore = rewardData.unclaimedRewards > 0;
        
        if (canAddMore) {
          // Enable button - there are available slots
          addButton.disabled = false;
          addButton.classList.remove('tier-reward-product__add-btn--added');
          
          const addText = addButton.querySelector('.tier-reward-product__add-text');
          if (addText) {
            addText.textContent = 'Add';
          }
          

        } else {
          // Disable button - no slots available
          addButton.disabled = true;
          addButton.classList.add('tier-reward-product__add-btn--added');
          
          const addText = addButton.querySelector('.tier-reward-product__add-text');
          if (addText) {
              addText.textContent = 'All Slots Filled';
          }
          

        }
      });
    }
  
    // Enhanced loading state methods for tier rewards
    showTierRewardLoading(product, button) {
      if (!button || !product) {
        console.error('❌ showTierRewardLoading: missing elements');
        return;
      }
      
      // Button loading state
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      
      button.classList.add('tier-reward-product__add-btn--loading');
      button.disabled = true;
      
      if (buttonText) buttonText.style.opacity = '0';
      if (buttonLoading) buttonLoading.style.opacity = '1';
      
      // Individual item status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.style.display = 'flex';
        statusIndicator.classList.remove('is-success');
        statusIndicator.classList.add('is-loading');
      }
      

    }
    
    showTierRewardSuccess(product, button) {
      if (!button || !product) return;
      
      // Button success state
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      const buttonSuccess = button.querySelector('.tier-reward-product__add-success');
      
      button.classList.remove('tier-reward-product__add-btn--loading');
      button.classList.add('tier-reward-product__add-btn--success');
      
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonSuccess) buttonSuccess.style.opacity = '1';
      
      // Individual item status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.classList.remove('is-loading');
        statusIndicator.classList.add('is-success');
      }
      

    }
    
    setTierRewardAdded(product, button) {
      if (!button || !product) return;
      
      // Reset button classes
      button.classList.remove('tier-reward-product__add-btn--loading', 'tier-reward-product__add-btn--success');
      button.classList.add('tier-reward-product__add-btn--added');
      button.disabled = true;
      
      // Reset button elements
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      const buttonSuccess = button.querySelector('.tier-reward-product__add-success');
      
      if (buttonText) {
        buttonText.style.opacity = '1';
        buttonText.textContent = 'Added';
      }
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonSuccess) buttonSuccess.style.opacity = '0';
      
      // Hide status indicator after delay
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        setTimeout(() => {
          statusIndicator.style.display = 'none';
          statusIndicator.classList.remove('is-loading', 'is-success');
        }, 1000);
      }
      

    }
    
    showTierRewardError(product, button, errorMessage) {
      if (!button || !product) return;
      
      // Button error state
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      
      button.classList.remove('tier-reward-product__add-btn--loading');
      button.classList.add('tier-reward-product__add-btn--error');
      
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonText) {
        buttonText.style.opacity = '1';
        buttonText.textContent = 'Error';
      }
      
      // Hide status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.style.display = 'none';
        statusIndicator.classList.remove('is-loading', 'is-success');
      }
      

    }
    
    resetTierRewardState(product, button) {
      if (!button || !product) return;
      
      // Reset all button states
      button.classList.remove(
        'tier-reward-product__add-btn--loading', 
        'tier-reward-product__add-btn--success', 
        'tier-reward-product__add-btn--error',
        'tier-reward-product__add-btn--added'
      );
      button.disabled = false;
      
      // Reset button elements
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      const buttonSuccess = button.querySelector('.tier-reward-product__add-success');
      
      if (buttonText) {
        buttonText.style.opacity = '1';
        buttonText.textContent = 'Add';
      }
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonSuccess) buttonSuccess.style.opacity = '0';
      
      // Hide status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.style.display = 'none';
        statusIndicator.classList.remove('is-loading', 'is-success');
      }
      

    }
  
    // Legacy methods for backward compatibility
    setButtonLoadingState(button, isLoading) {
      if (!button) {
        console.error('❌ setButtonLoadingState: button is null');
        return;
      }
      
      const addText = button.querySelector('.tier-reward-product__add-text');
      
      if (isLoading) {
        button.disabled = true;
        if (addText) addText.textContent = 'Adding...';
      } else {
        button.disabled = false;
        if (addText) addText.textContent = 'Add';
      }
    }
  
    setButtonSuccessState(button) {
      if (!button) return;
      const addText = button.querySelector('.tier-reward-product__add-text');
      if (addText) addText.textContent = 'Added ✓';
    }
  
    setButtonErrorState(button, errorMessage) {
      if (!button) return;
      const addText = button.querySelector('.tier-reward-product__add-text');
      if (addText) addText.textContent = 'Error';
    }
  
    // Modal Loading States
    showModalLoading() {
      const modal = document.querySelector('.tier-rewards-dropdown.is-active');
      if (!modal) return;
  
      // Add loading overlay to modal
      const loadingOverlay = this.createLoadingOverlay('Calculating available rewards...');
      loadingOverlay.classList.add('tier-modal-loading');
      modal.appendChild(loadingOverlay);
  

    }
  
    hideModalLoading() {
      const loadingOverlay = document.querySelector('.tier-modal-loading');
      if (loadingOverlay) {
        loadingOverlay.remove();

      }
    }
  
        // Progress Bar Loading States  
    showProgressLoading() {
      const progressBar = this.progressBar || document.querySelector('[data-tier-sale-progress]');
      if (progressBar) {
        progressBar.classList.add('tier-progress-loading');
      }
    }

    hideProgressLoading() {
      // Always try to find and clear progress bar loading
      const progressBar = this.progressBar || document.querySelector('[data-tier-sale-progress]');
      if (progressBar) {
        progressBar.classList.remove('tier-progress-loading');
      }
      
      // Safety net: clear any other progress bars that might be stuck
      const allProgressBars = document.querySelectorAll('[data-tier-sale-progress].tier-progress-loading');
      allProgressBars.forEach(bar => {
        bar.classList.remove('tier-progress-loading');
      });
    }
  
    // Dropdown Cart Loading States - Show when ANY cart change happens
    showDropdownCartLoading() {
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (!dropdown) return; // Only show if dropdown is open
  
      // Show the progress bar in the dropdown header
      const progressContainer = dropdown.querySelector('[data-tier-progress-container]');
      if (progressContainer) {
        progressContainer.style.display = 'block';
        progressContainer.classList.add('is-active');
        
        // Update progress bar to show loading state
        const progressFill = progressContainer.querySelector('[data-tier-progress-fill]');
        const progressText = progressContainer.querySelector('[data-tier-progress-text]');
        
        if (progressFill) {
          progressFill.style.width = '100%';
          progressFill.classList.add('tier-progress-loading');
        }
        
        if (progressText) {
          progressText.textContent = 'Updating rewards...';
        }
      }
  
      // Add shimmer effect to all products AND disable all buttons
      const products = dropdown.querySelectorAll('.tier-reward-product');
      products.forEach(product => {
        product.classList.add('tier-progress-loading');
        
        // Disable all add buttons during loading
        const addButton = product.querySelector('[data-tier-reward-add]');
        if (addButton) {
          addButton.disabled = true;
          addButton.classList.add('tier-reward-product__add-btn--loading-disabled');
        }
      });
  

    }
  
        hideDropdownCartLoading() {
      // Clear loading from dropdown if it exists
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (dropdown) {
        // Hide the progress bar in the dropdown header
        const progressContainer = dropdown.querySelector('[data-tier-progress-container]');
        if (progressContainer) {
          // Immediate hide without animation to prevent stuck states
          progressContainer.classList.remove('is-active');
          progressContainer.style.display = 'none';
          
          // Reset progress bar
          const progressFill = progressContainer.querySelector('[data-tier-progress-fill]');
          if (progressFill) {
            progressFill.style.width = '0%';
            progressFill.classList.remove('tier-progress-loading');
          }
        }

        // Remove shimmer effect from dropdown products
        const products = dropdown.querySelectorAll('.tier-reward-product');
        products.forEach(product => {
          product.classList.remove('tier-progress-loading');
          
          const addButton = product.querySelector('[data-tier-reward-add]');
          if (addButton) {
            addButton.classList.remove('tier-reward-product__add-btn--loading-disabled');
          }
        });
      }
      
      // ALSO clear loading from ANY products outside dropdown (safety net)
      const allProducts = document.querySelectorAll('.tier-reward-product.tier-progress-loading');
      allProducts.forEach(product => {
        product.classList.remove('tier-progress-loading');
        
        const addButton = product.querySelector('[data-tier-reward-add]');
        if (addButton) {
          addButton.classList.remove('tier-reward-product__add-btn--loading-disabled');
        }
      });
    }
  
  
  
    // Helper Methods for State Management
    clearUpdateState() {
      this.isUpdatingTiers = false;
      
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }
      
      if (this.stuckStateRecoveryTimeout) {
        clearTimeout(this.stuckStateRecoveryTimeout);
        this.stuckStateRecoveryTimeout = null;
      }
      

    }

    clearAllTimeouts() {
      if (this.cartChangeTimeout) {
        clearTimeout(this.cartChangeTimeout);
        this.cartChangeTimeout = null;
      }
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }
      if (this.stuckStateRecoveryTimeout) {
        clearTimeout(this.stuckStateRecoveryTimeout);
        this.stuckStateRecoveryTimeout = null;
      }

    }

    clearAllLoadingStates() {

      
      // Clear progress bar loading
      this.hideProgressLoading();
      
      // Clear dropdown cart loading
      this.hideDropdownCartLoading();
      
      // Clear modal loading
      this.hideModalLoading();
      
      // Force remove any stuck classes from progress bar
      const progressBar = document.querySelector('[data-tier-sale-progress]');
      if (progressBar) {
        progressBar.classList.remove('tier-progress-loading');
      }
      
      // Force remove loading classes from all products
      const products = document.querySelectorAll('.tier-reward-product');
      products.forEach(product => {
        product.classList.remove('tier-progress-loading');
        
        const addButton = product.querySelector('[data-tier-reward-add]');
        if (addButton) {
          addButton.classList.remove('tier-reward-product__add-btn--loading-disabled');
        }
      });
      
      // Clear any progress containers
      const progressContainer = document.querySelector('[data-tier-progress-container]');
      if (progressContainer) {
        progressContainer.style.display = 'none';
        progressContainer.classList.remove('is-active');
        
        const progressFill = progressContainer.querySelector('[data-tier-progress-fill]');
        if (progressFill) {
          progressFill.style.width = '0%';
          progressFill.classList.remove('tier-progress-loading');
        }
      }
      
      // Remove any loading overlays
      const loadingOverlays = document.querySelectorAll('.tier-loading-overlay, .tier-modal-loading');
      loadingOverlays.forEach(overlay => overlay.remove());
      

    }
  
    forceRecoveryFromStuckState() {
      this.logDebug('🚨 Forcing recovery from stuck state...');
      
      // Clear all timeouts first
      this.clearAllTimeouts();
      
      // Clear all state
      this.clearUpdateState();
      
      // Use comprehensive loading state cleanup
      this.clearAllLoadingStates();
      
      this.logDebug('✅ Recovery completed - system should be responsive again');
      
      // Try a gentle retry after a delay
      setTimeout(() => {
        if (!this.isUpdatingTiers) {
          this.logDebug('🔄 Attempting gentle retry after recovery...');
          this.updateTierProgress().catch(error => {
            this.logDebug('⚠️ Gentle retry failed, but system is still recovered:', error.message);
          });
        }
      }, 2000);
    }
  
    createLoadingOverlay(message = 'Loading...') {
      const overlay = document.createElement('div');
      overlay.className = 'tier-loading-overlay';
      overlay.innerHTML = `
        <div class="tier-loading-content">
          <div class="tier-loading-spinner">
            <svg width="32" height="32" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="75.4" stroke-dashoffset="75.4" opacity="0.4"/>
              <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="75.4" stroke-dashoffset="56.5"/>
            </svg>
          </div>
          <p class="tier-loading-text">${message}</p>
        </div>
      `;
      return overlay;
    }
  
  
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.tierSaleTracker = new TierSaleTracker();
    });
  } else {
    window.tierSaleTracker = new TierSaleTracker();
  }
  
  // Also initialize on page navigation (for SPA-like themes)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      if (window.tierSaleTracker) {
        window.tierSaleTracker.forceRecoveryFromStuckState();
      }
      window.tierSaleTracker = new TierSaleTracker();
    }, 100);
  });
  
  // Add manual test and recovery functions for debugging
  window.testTierSaleTracker = function() {
    console.log('=== Testing TierSaleTracker ===');
    console.log('Tracker exists:', !!window.tierSaleTracker);
    console.log('Is initialized:', window.tierSaleTracker?.isInitialized);
    console.log('Progress bar found:', !!window.tierSaleTracker?.progressBar);
    console.log('Is updating:', window.tierSaleTracker?.isUpdatingTiers);
    
    if (window.tierSaleTracker) {
      window.tierSaleTracker.updateTierProgress();
    }
  };
  
  // Manual recovery function for stuck states
  window.recoverTierSaleTracker = function() {
    console.log('=== Manual Recovery Triggered ===');
    if (window.tierSaleTracker) {
      window.tierSaleTracker.forceRecoveryFromStuckState();
    } else {
      console.log('❌ TierSaleTracker not found');
    }
  };

  // Manual function to clear just loading states
  window.clearTierSaleLoading = function() {
    console.log('=== Manual Loading State Clear ===');
    if (window.tierSaleTracker) {
      window.tierSaleTracker.clearAllLoadingStates();
    } else {
      console.log('❌ TierSaleTracker not found');
    }
  }; 